import { useEffect, useReducer, useState } from "react"

import type {
  AnimationEvent,
  AnimationPlayer,
  PlaybackSpeed,
  PositionChange,
} from "@/types"

import { applyEventToPitchState, applyPositionChange } from "./pitch-helpers"
import type { PitchState } from "./use-pitch-state"

// Re-export so consumers can import PlaybackSpeed from this module.
export type { PlaybackSpeed }

// ─── Constants ──────────────────────────────────────────────────────────────

/** Base interval in milliseconds at 1x speed. */
export const BASE_DELAY_MS = 800

/** Delay between sub-step animation frames at 1x speed. */
export const SUB_STEP_DELAY_MS = 120

// ─── State ──────────────────────────────────────────────────────────────────

export interface ReplayState {
  /** Current event index (0 = kickoff). */
  currentEventIndex: number
  /** Whether auto-advance is active. */
  isPlaying: boolean
  /** Pitch state: player positions, ball. */
  pitchState: PitchState
  /** Stack of previous pitch states for STEP_BACK support. */
  historyStack: PitchState[]
  /** Frames for multi-step animation, or null when not animating. */
  animationFrames: PositionChange[][] | null
  /** Current frame index within animationFrames. */
  animationFrameIndex: number
}

// ─── Actions ────────────────────────────────────────────────────────────────

export type ReplayAction =
  | {
      type: "TICK"
      events: AnimationEvent[]
      players: Record<string, AnimationPlayer>
    }
  | {
      type: "STEP_FORWARD_MANUAL"
      events: AnimationEvent[]
      players: Record<string, AnimationPlayer>
    }
  | { type: "STEP_BACK" }
  | {
      type: "SEEK_TO"
      index: number
      events: AnimationEvent[]
      players: Record<string, AnimationPlayer>
      initialPitchState: PitchState
    }
  | { type: "PLAY" }
  | { type: "PAUSE" }
  | { type: "TOGGLE_PLAY" }

// ─── Frame helpers ──────────────────────────────────────────────────────────

/**
 * Groups position changes into animation frames.
 *
 * Each spatial change (move/push/follow_up) starts a new frame.
 * Non-spatial changes (status/ball) are appended to the preceding frame
 * so they apply simultaneously with the spatial change they describe
 * (e.g., push + status "prone" in the same frame).
 */
export function buildAnimationFrames(
  changes: PositionChange[],
): PositionChange[][] {
  const frames: PositionChange[][] = []

  for (const change of changes) {
    const isSpatial =
      change.type === "move" ||
      change.type === "push" ||
      change.type === "follow_up"

    if (isSpatial) {
      frames.push([change])
    } else if (frames.length > 0) {
      frames[frames.length - 1].push(change)
    } else {
      frames.push([change])
    }
  }

  return frames
}

// ─── Pure reducer ───────────────────────────────────────────────────────────

export function replayReducer(
  state: ReplayState,
  action: ReplayAction,
): ReplayState {
  switch (action.type) {
    case "TICK": {
      if (!state.isPlaying) return state
      return advanceOneStep(state, action.events, action.players)
    }

    case "STEP_FORWARD_MANUAL": {
      // Manual step: same logic as TICK but works even when paused.
      // If currently animating, ignore (block during animation).
      if (state.animationFrames) return state
      return advanceOneStep(state, action.events, action.players)
    }

    case "STEP_BACK": {
      // Cancel any in-progress animation by reverting to history
      if (state.animationFrames) {
        // Animation in progress — revert to pre-animation state from history
        if (state.historyStack.length === 0) return state
        const prev = state.historyStack[state.historyStack.length - 1]
        // The event index was already advanced when animation started,
        // so step it back by 1.
        return {
          ...state,
          currentEventIndex: state.currentEventIndex - 1,
          isPlaying: false,
          pitchState: prev,
          historyStack: state.historyStack.slice(0, -1),
          animationFrames: null,
          animationFrameIndex: 0,
        }
      }

      if (state.currentEventIndex <= 0) return state
      if (state.historyStack.length === 0) return state

      const prev = state.historyStack[state.historyStack.length - 1]
      return {
        ...state,
        currentEventIndex: state.currentEventIndex - 1,
        isPlaying: false,
        pitchState: prev,
        historyStack: state.historyStack.slice(0, -1),
        animationFrames: null,
        animationFrameIndex: 0,
      }
    }

    case "SEEK_TO": {
      const { events, players, initialPitchState } = action
      const clamped = Math.max(0, Math.min(action.index, events.length - 1))

      // Replay from kickoff state (events[0]) through events[clamped]
      let pitchState = initialPitchState
      for (let i = 1; i <= clamped; i++) {
        pitchState = applyEventToPitchState(pitchState, events[i], players)
      }

      return {
        ...state,
        currentEventIndex: clamped,
        pitchState,
        historyStack: [],
        animationFrames: null,
        animationFrameIndex: 0,
      }
    }

    case "PLAY":
      return { ...state, isPlaying: true }

    case "PAUSE":
      return { ...state, isPlaying: false }

    case "TOGGLE_PLAY":
      return { ...state, isPlaying: !state.isPlaying }
  }
}

// ─── Internal step logic ────────────────────────────────────────────────────

/**
 * Advances the replay by one step. Handles both normal event application
 * and multi-frame animation advancement.
 */
function advanceOneStep(
  state: ReplayState,
  events: AnimationEvent[],
  players: Record<string, AnimationPlayer>,
): ReplayState {
  // If we're currently in a multi-frame animation, advance the animation
  if (state.animationFrames) {
    return advanceAnimation(state, events)
  }

  // Otherwise, step to the next event
  const nextIndex = state.currentEventIndex + 1
  if (nextIndex >= events.length) {
    // Already at last event — auto-pause
    return { ...state, isPlaying: false }
  }

  const event = events[nextIndex]
  const isLast = nextIndex >= events.length - 1

  // Check for multi-frame animation
  const frames = event.pitchSnapshot
    ? []
    : buildAnimationFrames(event.positionChanges)

  if (frames.length > 1) {
    // Start multi-frame animation: apply first frame, queue the rest
    let pitchState = state.pitchState
    for (const change of frames[0]) {
      pitchState = applyPositionChange(pitchState, change)
    }

    return {
      ...state,
      currentEventIndex: nextIndex,
      pitchState,
      historyStack: [...state.historyStack, state.pitchState],
      animationFrames: frames,
      animationFrameIndex: 1,
    }
  }

  // Single frame or snapshot — apply atomically
  const nextPitchState = applyEventToPitchState(
    state.pitchState,
    event,
    players,
  )

  return {
    ...state,
    currentEventIndex: nextIndex,
    isPlaying: isLast ? false : state.isPlaying,
    pitchState: nextPitchState,
    historyStack: [...state.historyStack, state.pitchState],
    animationFrames: null,
    animationFrameIndex: 0,
  }
}

/**
 * Advances one frame within a multi-frame animation.
 */
function advanceAnimation(
  state: ReplayState,
  events: AnimationEvent[],
): ReplayState {
  const frames = state.animationFrames!
  const frameIndex = state.animationFrameIndex

  if (frameIndex >= frames.length) {
    // Animation complete — check if we should auto-pause
    const isLast = state.currentEventIndex >= events.length - 1
    return {
      ...state,
      isPlaying: isLast ? false : state.isPlaying,
      animationFrames: null,
      animationFrameIndex: 0,
    }
  }

  // Apply the next frame
  let pitchState = state.pitchState
  for (const change of frames[frameIndex]) {
    pitchState = applyPositionChange(pitchState, change)
  }

  const nextFrameIndex = frameIndex + 1
  const animationDone = nextFrameIndex >= frames.length

  if (animationDone) {
    const isLast = state.currentEventIndex >= events.length - 1
    return {
      ...state,
      pitchState,
      isPlaying: isLast ? false : state.isPlaying,
      animationFrames: null,
      animationFrameIndex: 0,
    }
  }

  return {
    ...state,
    pitchState,
    animationFrameIndex: nextFrameIndex,
  }
}

// ─── Derived state ──────────────────────────────────────────────────────────

export interface ReplayDerived {
  /** The current animation event, or null. */
  currentEvent: AnimationEvent | null
  /** True when a multi-frame animation is in progress. */
  isAnimating: boolean
}

export function computeDerivedState(
  state: ReplayState,
  events: AnimationEvent[],
): ReplayDerived {
  return {
    currentEvent: events[state.currentEventIndex] ?? null,
    isAnimating: state.animationFrames !== null,
  }
}

// ─── Initial state builder ──────────────────────────────────────────────────

export function buildInitialReplayState(
  initialPitchState: PitchState,
): ReplayState {
  return {
    currentEventIndex: 0,
    isPlaying: false,
    pitchState: initialPitchState,
    historyStack: [],
    animationFrames: null,
    animationFrameIndex: 0,
  }
}

// ─── Hook wrapper ───────────────────────────────────────────────────────────

/**
 * Unified replay reducer hook. Owns all replay state: pitch state, playback
 * index, play/pause, and multi-frame animation. The interval callback only
 * calls `dispatch({ type: "TICK", events, players })` — dispatch is
 * referentially stable, so no refs are needed to avoid stale closures.
 */
export function useReplayReducer(
  events: AnimationEvent[],
  players: Record<string, AnimationPlayer>,
  initialPitchState: PitchState,
) {
  const [state, dispatch] = useReducer(
    replayReducer,
    initialPitchState,
    buildInitialReplayState,
  )

  const [speed, setSpeed] = useState<PlaybackSpeed>(1)

  const derived = computeDerivedState(state, events)

  // ─── Auto-advance via setInterval ───────────────────────────────────

  useEffect(() => {
    if (!state.isPlaying) return

    // Use faster interval during animation, slower during normal playback
    const delay = derived.isAnimating
      ? Math.round(SUB_STEP_DELAY_MS / speed)
      : Math.round(BASE_DELAY_MS / speed)

    const id = setInterval(() => {
      dispatch({ type: "TICK", events, players })
    }, delay)

    return () => clearInterval(id)
  }, [state.isPlaying, derived.isAnimating, speed, events, players])

  // ─── Action creators ────────────────────────────────────────────────

  const play = () => dispatch({ type: "PLAY" })
  const pause = () => dispatch({ type: "PAUSE" })
  const togglePlay = () => dispatch({ type: "TOGGLE_PLAY" })

  const stepForward = () =>
    dispatch({ type: "STEP_FORWARD_MANUAL", events, players })

  const stepBack = () => dispatch({ type: "STEP_BACK" })

  const seekTo = (index: number) =>
    dispatch({ type: "SEEK_TO", index, events, players, initialPitchState })

  return {
    state,
    derived,
    speed,
    play,
    pause,
    togglePlay,
    stepForward,
    stepBack,
    seekTo,
    setSpeed,
  }
}
